import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, switchMap, of, take } from 'rxjs';
import { environment } from 'environments/environment';

export interface UserPreferences {
    favoritos: string[];
    recientes: string[];
}

@Injectable({
    providedIn: 'root'
})
export class UserPreferencesService {
    private _preferences: BehaviorSubject<UserPreferences | null> = new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for preferences
     */
    get preferences$(): Observable<UserPreferences | null> {
        return this._preferences.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getPreferences(): Observable<UserPreferences> {
        return this._httpClient.get<UserPreferences>(environment.urlBase + 'empleado/preferences/current').pipe(
            tap((response) => {
                this._preferences.next(response);
            })
        );
    }

    addToFavorites(itemId: string): Observable<UserPreferences> {
        const current = this._preferences.value || { favoritos: [], recientes: [] };
        if (!current.favoritos.includes(itemId)) {
            const updated = {
                ...current,
                favoritos: [...(current.favoritos || []), itemId]
            };
            return this.updatePreferences(updated);
        }
        return of(current);
    }

    removeFromFavorites(itemId: string): Observable<UserPreferences> {
        const current = this._preferences.value || { favoritos: [], recientes: [] };
        const updated = {
            ...current,
            favoritos: (current.favoritos || []).filter(id => id !== itemId)
        };
        return this.updatePreferences(updated);
    }

    toggleFavorite(itemId: string): Observable<UserPreferences> {
        const current = this._preferences.value || { favoritos: [], recientes: [] };
        if (current.favoritos.includes(itemId)) {
            return this.removeFromFavorites(itemId);
        } else {
            return this.addToFavorites(itemId);
        }
    }

    addToRecents(itemId: string): Observable<UserPreferences> {
        const current = this._preferences.value || { favoritos: [], recientes: [] };
        let newRecents = [...(current.recientes || [])];

        // Remove if exists
        newRecents = newRecents.filter(id => id !== itemId);

        // Add to front
        newRecents.unshift(itemId);

        // Limit to 10
        if (newRecents.length > 10) {
            newRecents = newRecents.slice(0, 10);
        }

        const updated = {
            ...current,
            recientes: newRecents
        };
        return this.updatePreferences(updated);
    }

    updatePreferences(prefs: UserPreferences): Observable<UserPreferences> {
        return this._httpClient.put<UserPreferences>(environment.urlBase + 'empleado/preferences/current', prefs).pipe(
            tap((response) => {
                this._preferences.next(response);
            })
        );
    }
}
